import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { LuIceCreamBowl } from 'react-icons/lu';
import { useAuth } from '../context/AuthContext';

const Nav = styled.nav`
  background: ${props => props.scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.98)'};
  padding: ${props => props.scrolled ? '1rem 2rem' : '1.8rem 2rem'};
  box-shadow: ${props => props.scrolled ? '0 5px 20px rgba(0,0,0,0.1)' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.3s ease-in-out;
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255,107,107,0.1);
`;

const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1rem;
`;

const Logo = styled(Link)`
  font-size: 2rem;
  font-weight: 800;
  color: #ff6b6b;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: -1px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: left;

  &:hover {
    transform: translateY(-2px);
    text-shadow: 2px 2px 15px rgba(255,107,107,0.4);
  }

  span.text {
    background: linear-gradient(135deg, #ff6b6b 0%, #ff466a 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    position: relative;
    display: inline-block;
    animation: floatText 3s ease-in-out infinite;

    @keyframes floatText {
      0%, 100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-5px);
      }
    }

    &::after {
      content: attr(data-text);
      position: absolute;
      left: 0;
      top: 0;
      z-index: -1;
      background: linear-gradient(135deg, #ff6b6b 0%, #ff466a 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      filter: blur(10px);
      opacity: 0.5;
      transform-origin: bottom;
      animation: glowPulse 2s infinite;
    }

    @keyframes glowPulse {
      0%, 100% {
        filter: blur(10px) brightness(1);
        opacity: 0.5;
      }
      50% {
        filter: blur(15px) brightness(1.5);
        opacity: 0.8;
      }
    }
  }

  span.dot {
    color: #ff6b6b;
    font-size: 2.5rem;
    margin: 0 0.2rem;
    display: inline-block;
    animation: popEffect 2s infinite;
    text-shadow: 0 0 10px rgba(255,107,107,0.3);

    @keyframes popEffect {
      0%, 100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.2);
      }
    }
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    padding: 1rem;
    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  }
`;

const NavLink = styled(Link)`
  color: #333;
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: 500;
  padding: 0.5rem;
  transition: color 0.3s ease;

  &:hover {
    color: #ff6b6b;
  }

  &.active {
    color: #ff6b6b;
  }
`;

const AdminLink = styled(NavLink)`
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MenuIcon = styled.div`
  width: 25px;
  height: 2px;
  background: #333;
  position: relative;
  transition: all 0.3s ease;
  transform: ${props => props.isOpen ? 'rotate(45deg)' : 'rotate(0)'};

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 25px;
    height: 2px;
    background: #333;
    transition: all 0.3s ease;
  }

  &::before {
    top: ${props => props.isOpen ? '0' : '-8px'};
    transform: ${props => props.isOpen ? 'rotate(0)' : 'rotate(0)'};
    opacity: ${props => props.isOpen ? '0' : '1'};
  }

  &::after {
    bottom: ${props => props.isOpen ? '0' : '-8px'};
    transform: ${props => props.isOpen ? 'rotate(-90deg)' : 'rotate(0)'};
  }
`;

const IceCreamIcon = styled(LuIceCreamBowl)`
  width: 2.4rem;
  height: 2.4rem;
  margin-left: 0.5rem;
  color: #ff6b6b;
`;

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Nav scrolled={scrolled}>
      <NavContainer>
        <Logo to="/">
          <IceCreamIcon />
          <span className="text" data-text="Ice">OLDE</span>
          <span className="text" data-text="Ice">TOWN</span>
          <span className="dot">•</span>
          <span className="text" data-text="Ice">Ice</span>
          <span className="dot">•</span>
          <span className="text" data-text="Cream">Cream</span>
        </Logo>
        <MenuButton onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu">
          <MenuIcon isOpen={isOpen} />
        </MenuButton>
        <NavLinks isOpen={isOpen}>
          <NavLink to="/" className={location.pathname === '/' ? 'active' : ''}>
            Home
          </NavLink>
          <NavLink to="/menu" className={location.pathname === '/menu' ? 'active' : ''}>
            Menu
          </NavLink>
          <NavLink to="/order" className={location.pathname === '/order' ? 'active' : ''}>
            Order
          </NavLink>
          <NavLink to="/contact" className={location.pathname === '/contact' ? 'active' : ''}>
            Contact
          </NavLink>
          {isAuthenticated ? (
            <AdminLink to="/admin/orders">Admin Dashboard</AdminLink>
          ) : (
            <AdminLink to="/admin/login">Admin Login</AdminLink>
          )}
        </NavLinks>
      </NavContainer>
    </Nav>
  );
}

export default Navbar;
